import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";

const SECTIONS = [
  {
    title: "1. When this policy applies",
    body: "This policy applies when an AI voice agent configured through Savy Kerno handles a telephone call or browser voice conversation for a business customer. It explains what callers are told, what is recorded, and who is responsible for what.",
  },
  {
    title: "2. Callers are always told it is an AI",
    body: "Every AI agent begins by introducing itself as an AI assistant acting for the business. This disclosure is built into the product and cannot be switched off. Businesses must not configure greetings that suggest the caller is speaking to a human.",
  },
  {
    title: "3. Recording default: off",
    body: "Call audio recording is disabled by default across the platform. For AI-handled conversations we store the call time, caller number, duration, outcome and a text transcript. If audio recording is ever enabled, callers must be informed at the start of the call and a lawful basis must exist.",
  },
  {
    title: "4. Who is responsible for what",
    body: "The business using Savy Kerno is the data controller for its callers' data: it decides why calls are handled, what the agent says, and how long records are kept. Savy Kerno acts as the processor, handling that data only on the business's instructions and to provide the service. The business is responsible for having a lawful basis (for example, answering an inbound enquiry) and for any consent needed for follow-up calls or marketing.",
  },
  {
    title: "5. Outbound and marketing calls",
    body: "UK rules (PECR) require prior specific consent for automated calling systems to make marketing calls, and live marketing calls must screen against the Telephone Preference Service (TPS/CTPS). Savy Kerno's outbound calling features remain disabled unless and until these requirements can be met, and nothing in the platform makes automated marketing calls today.",
  },
  {
    title: "6. Transfers to people",
    body: "If a business configures a transfer number, callers who ask for a person can be transferred. The business must ensure the transfer destination is accurate and that staff answering transferred calls know the call came via an AI agent.",
  },
  {
    title: "7. Retention",
    body: "Call records and transcripts are kept while the business account is active and can be deleted by the business from the dashboard at any time. Businesses should define their own retention period appropriate to their obligations.",
  },
  {
    title: "8. Review status",
    body: "This policy is a working draft pending qualified legal review before paid launch. It describes implemented product behaviour and does not constitute legal advice.",
  },
];

export default function AiCallingPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="ai-calling-policy-page">
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-[#111111]">
          <BrandLogo size={22} /> <span className="font-semibold">Savy Kerno</span>
        </Link>
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">AI Calling & Recording Policy</h1>
        <p className="mt-3 text-sm text-neutral-500">Last updated November 2026 · Draft pending legal review</p>
        <div className="mt-8 space-y-6">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-base font-bold text-[#111111]">{s.title}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{s.body}</p>
            </section>
          ))}
        </div>
        <div className="mt-12 flex gap-4 border-t border-neutral-100 pt-6 text-xs text-neutral-400">
          <Link to="/privacy" className="hover:text-[#111111]">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-[#111111]">Terms of Service</Link>
          <Link to="/data-processing" className="hover:text-[#111111]">Data Processing Agreement</Link>
          <Link to="/cookies" className="hover:text-[#111111]">Cookie Policy</Link>
        </div>
      </div>
    </div>
  );
}
